///
/// EarthBrowser
///
/// Created by Matt Giger on 10/31/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Webcam contact sheet: the cameras of a cluster (long-press / right-click
/// on a cluster marker) or near the focused camera (its panel's "Browse
/// all") as a wall of live thumbnails. Nobody wants to hunt hundreds of
/// freeway dots across a metro; they scan the pictures, filter by name or
/// category, and tap the one they want, which flies there and opens its
/// panel. Driven by dialog.webcamSheet (ephemeral).
///

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { WebcamStationsTable } from "@/earthbrowser/dataset-distribution/webcams/WebcamStationsTable"
import { setWebcamSheet, type WebcamSheetState } from "@/redux/dialogSlice"
import { sendCameraCommand, sendDatasetAction } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { datasetUrl } from "@/utils/datasetUrl"
import { cn } from "@/utils/utils"
import type { WebcamCategory, WebcamEntry } from "@shared-types/webcam"
import { Crosshair, Play, Search, Video } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

// Cards rendered per "Show more" step; a 5k-camera cluster shouldn't mount
// 5k images at once
const cPageSize = 120
// Camera altitude that frames an area: extent fills ~70% of the half-height
// at the 40° field of view (1 / tan 20° ≈ 2.75)
const cFrameFocal = 2.75 / 0.7
const cMinFrameAltitudeKm = 1.5

const cCategoryLabels: Record<WebcamCategory, string> = {
	traffic: "Traffic",
	scenic: "Scenic",
	weather: "Weather",
	marine: "Marine",
	aviation: "Aviation",
	wildlife: "Wildlife",
}

// Category chips in the webcam family (design-system/03-hud-canvas.md), on
// a dark base so they read over bright skies
const cCategoryChip: Record<WebcamCategory, string> = {
	traffic: "text-purple-200 border-purple-300/40",
	scenic: "text-fuchsia-300 border-fuchsia-400/40",
	weather: "text-pink-300 border-pink-400/40",
	marine: "text-rose-300 border-rose-400/40",
	aviation: "text-purple-300 border-purple-400/40",
	wildlife: "text-green-300 border-green-400/40",
}

function thumbUrl(cam: WebcamEntry): string {
	const stamp = cam.last_success ? new Date(cam.last_success).getTime() : 0
	return `${datasetUrl(cam.thumb_url)}?eb=${stamp}`
}

/** Top `n` most frequent non-empty values */
function topValues(values: Array<string | null | undefined>, n: number): string[] {
	const counts = new Map<string, number>()
	for (const v of values) {
		if (v) counts.set(v, (counts.get(v) ?? 0) + 1)
	}
	return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, n).map(([v]) => v)
}

export function WebcamContactSheet() {
	const dispatch = useAppDispatch()
	const sheet = useAppSelector(state => state.dialog.webcamSheet)
	const webcamsVisible = useAppSelector(state => state.earthbrowser.visibleDatasets.includes("webcam"))

	if (!sheet) return null
	return (
		<Dialog open onOpenChange={open => { if (!open) dispatch(setWebcamSheet(null)) }}>
			<DialogContent
				className="overflow-hidden p-0 w-[94vw] max-w-6xl h-[84vh] max-sm:w-screen max-sm:max-w-none max-sm:h-[100dvh] max-sm:rounded-none rounded-3xl border-2 border-slate-700 bg-transparent text-white"
				aria-describedby="webcam-sheet-description"
			>
				<SheetBody sheet={sheet} webcamsVisible={webcamsVisible} />
			</DialogContent>
		</Dialog>
	)
}

function SheetBody({ sheet, webcamsVisible }: { sheet: WebcamSheetState, webcamsVisible: boolean }) {
	const dispatch = useAppDispatch()
	const [entries, setEntries] = useState<WebcamEntry[] | null>(null)
	const [query, setQuery] = useState("")
	const [category, setCategory] = useState<WebcamCategory | null>(null)
	const [liveOnly, setLiveOnly] = useState(false)
	const [shown, setShown] = useState(cPageSize)
	const gridRef = useRef<HTMLDivElement>(null)

	// Rows in the sheet's order (cluster rank or distance), not the table's
	useEffect(() => {
		let cancelled = false
		setEntries(null)
		setShown(cPageSize)
		void WebcamStationsTable.getByIds(sheet.ids).then(rows => {
			if (cancelled) return
			const byId = new Map(rows.map(r => [r.id, r]))
			setEntries(sheet.ids.map(id => byId.get(id)).filter((r): r is WebcamEntry => !!r))
		})
		return () => { cancelled = true }
	}, [sheet.ids])

	const categories = useMemo(() => {
		const present = new Set<WebcamCategory>()
		for (const e of entries ?? []) present.add(e.category)
		return (Object.keys(cCategoryLabels) as WebcamCategory[]).filter(c => present.has(c))
	}, [entries])

	const subtitle = useMemo(() => {
		if (!entries || entries.length === 0) return ""
		const regions = topValues(entries.map(e => e.region ?? e.country_code), 2)
		const sources = topValues(entries.map(e => e.attribution), 2)
		const parts = [regions.join(" · "), sources.join(", ")].filter(Boolean)
		return parts.join(" — ")
	}, [entries])

	const filtered = useMemo(() => {
		if (!entries) return []
		const needle = query.trim().toLowerCase()
		return entries.filter(e =>
			(!category || e.category === category) &&
			(!liveOnly || !!e.stream_url) &&
			(!needle || e.name.toLowerCase().includes(needle) || (e.region ?? "").toLowerCase().includes(needle) || e.attribution.toLowerCase().includes(needle))
		)
	}, [entries, query, category, liveOnly])

	// Filter changes restart paging from the top
	useEffect(() => {
		setShown(cPageSize)
		gridRef.current?.scrollTo({ top: 0 })
	}, [query, category, liveOnly])

	const close = () => dispatch(setWebcamSheet(null))

	const select = (id: string) => {
		close()
		if (!webcamsVisible) dispatch(sendDatasetAction({ type: "webcam", action: "enable" }))
		dispatch(sendDatasetAction({ type: "webcam", action: "selectId", id }))
	}

	const zoomToArea = () => {
		close()
		dispatch(sendDatasetAction({ type: "", action: "deselect" }))
		dispatch(sendCameraCommand({
			command: "flyTo/location",
			data: { lat: sheet.lat, lon: sheet.lon, altitude: Math.max(cMinFrameAltitudeKm, sheet.extentKm * cFrameFocal) },
		}))
	}

	const visible = filtered.slice(0, shown)

	return (
		<div className="flex h-full flex-col bg-background/95 backdrop-blur-xl">
			{/* Header */}
			<div className="flex items-start gap-3 px-5 pt-5 pb-3 pr-14">
				<div className="relative shrink-0">
					<div className="absolute inset-0 rounded-lg bg-gradient-to-br from-fuchsia-600 via-pink-500 to-fuchsia-500 blur-md opacity-75" />
					<div className="relative rounded-lg bg-fuchsia-500/20 p-2">
						<Video className="h-4 w-4 text-fuchsia-300" aria-hidden />
					</div>
				</div>
				<div className="min-w-0 flex-1">
					<DialogTitle className="text-base font-bold text-white">{sheet.title}</DialogTitle>
					<DialogDescription id="webcam-sheet-description" className="truncate text-xs text-slate-400">
						{subtitle || "Live views from this area"}
					</DialogDescription>
				</div>
				<button
					type="button"
					onClick={zoomToArea}
					className="hidden sm:flex items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-3 py-1.5 text-xs text-slate-200 hover:bg-white/10 hover:text-white transition-colors"
				>
					<Crosshair className="h-3.5 w-3.5 text-fuchsia-300" aria-hidden />
					Zoom to area
				</button>
			</div>

			{/* Filters */}
			<div className="flex flex-wrap items-center gap-2 px-5 pb-3">
				<label className="flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3 py-1.5">
					<Search className="h-3.5 w-3.5 text-slate-400" aria-hidden />
					<input
						value={query}
						onChange={e => setQuery(e.target.value)}
						placeholder="Filter by name, region or source"
						aria-label="Filter webcams"
						className="w-56 max-w-[60vw] bg-transparent text-xs text-white placeholder:text-slate-500 outline-none"
					/>
				</label>
				{categories.length > 1 && (
					<div className="flex items-center gap-1 rounded-full border border-white/10 bg-black/30 p-1">
						<FilterChip active={category === null} onClick={() => setCategory(null)}>All</FilterChip>
						{categories.map(c => (
							<FilterChip key={c} active={category === c} onClick={() => setCategory(category === c ? null : c)}>
								{cCategoryLabels[c]}
							</FilterChip>
						))}
					</div>
				)}
				<label className="flex items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-3 py-1.5">
					<Switch checked={liveOnly} onCheckedChange={setLiveOnly} className="scale-75 data-[state=checked]:bg-fuchsia-600" />
					<Play className="h-3 w-3 text-fuchsia-400" aria-hidden />
					<span className="text-xs text-white/80">Streams</span>
				</label>
				<span className="ml-auto text-[11px] text-slate-500">
					{entries
						? filtered.length === entries.length
							? `${entries.length.toLocaleString("en-US")} cameras`
							: `${filtered.length.toLocaleString("en-US")} of ${entries.length.toLocaleString("en-US")} cameras`
						: "Loading…"}
				</span>
			</div>

			{/* Grid */}
			<div ref={gridRef} className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
				{entries === null ? (
					<div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(176px,1fr))]">
						{Array.from({ length: 12 }, (_, i) => (
							<div key={i} className="aspect-video animate-pulse rounded-xl bg-slate-800/60" />
						))}
					</div>
				) : filtered.length === 0 ? (
					<div className="flex h-full items-center justify-center text-sm text-slate-400">
						No cameras match
					</div>
				) : (
					<>
						<div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(176px,1fr))]">
							{visible.map(cam => (
								<CameraCard key={cam.id} cam={cam} current={cam.id === sheet.currentId} onSelect={() => select(cam.id)} />
							))}
						</div>
						{shown < filtered.length && (
							<div className="flex justify-center pt-4">
								<button
									type="button"
									onClick={() => setShown(n => n + cPageSize)}
									className="rounded-full border border-white/10 bg-black/30 px-4 py-1.5 text-xs text-slate-200 hover:bg-white/10 hover:text-white transition-colors"
								>
									Show {Math.min(cPageSize, filtered.length - shown)} more
								</button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	)
}

function FilterChip({ active, onClick, children }: { active: boolean, onClick: () => void, children: React.ReactNode }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={active}
			className={cn(
				"rounded-full px-2.5 py-0.5 text-xs transition-colors",
				active ? "bg-fuchsia-500/30 text-white" : "text-white/70 hover:text-white hover:bg-white/10"
			)}
		>
			{children}
		</button>
	)
}

function CameraCard({ cam, current, onSelect }: { cam: WebcamEntry, current: boolean, onSelect: () => void }) {
	const stale = cam.status === "stale"
	return (
		<button
			type="button"
			onClick={onSelect}
			title={cam.name}
			aria-label={`Fly to ${cam.name}`}
			className={cn(
				"group relative aspect-video overflow-hidden rounded-xl border bg-slate-900 text-left transition-all duration-200",
				"hover:border-fuchsia-400 hover:shadow-lg hover:shadow-fuchsia-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400",
				current ? "border-fuchsia-400 ring-2 ring-fuchsia-400/60" : "border-slate-700"
			)}
		>
			<img
				src={thumbUrl(cam)}
				alt=""
				loading="lazy"
				decoding="async"
				className={cn(
					"absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]",
					stale && "grayscale opacity-70"
				)}
			/>
			<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-2.5 pb-2 pt-6">
				<div className="truncate text-xs font-medium text-white">{cam.name}</div>
				{cam.region && <div className="truncate text-[10px] text-slate-300">{cam.region}</div>}
			</div>
			<div className="absolute left-2 top-2 flex items-center gap-1">
				{cam.stream_url && !stale && (
					<span className="flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-100 backdrop-blur-sm">
						<span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
						Live
					</span>
				)}
				{stale && (
					<span className="rounded-full bg-black/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-300 backdrop-blur-sm">
						Stale
					</span>
				)}
			</div>
			<span className={cn("absolute right-2 top-2 rounded-full border bg-black/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider backdrop-blur-sm", cCategoryChip[cam.category])}>
				{cCategoryLabels[cam.category]}
			</span>
		</button>
	)
}
